/**
 * Enrollments API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for Enrollments with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  EnrollmentCreate,
  EnrollmentFilters,
  EnrollmentListResponse,
  EnrollmentResponse,
  EnrollmentUpdate,
  EnrollmentUpdateInput,
} from "@/types/enrollment"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of enrollments with permission checks
 */
export function useEnrollments(filters: EnrollmentFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["enrollments", filters],
    queryFn: async (): Promise<EnrollmentListResponse> => {
      const response = await api.enrollments.readEnrollmentsApiV1EnrollmentsGet(
        {
          limit: filters.limit,
          skip: filters.skip,
          search: filters.search,
          sortBy: filters.sort_by,
          sortOrder: filters.sort_order,
          createdBy: filters.created_by,
          createdAfter: filters.created_after,
          createdBefore: filters.created_before,
          updatedAfter: filters.updated_after,
          updatedBefore: filters.updated_before,
        },
      )

      return unwrapStandardResponse<EnrollmentListResponse>(
        response,
      ) as EnrollmentListResponse
    },
    enabled:
      hasPermission("enrollments:read") ||
      hasPermission("enrollments:read_own"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single enrollment by ID with permission checks
 */
export function useEnrollment(enrollmentId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["enrollments", enrollmentId],
    queryFn: async (): Promise<EnrollmentResponse> => {
      const response =
        await api.enrollments.readEnrollmentApiV1EnrollmentsEnrollmentIdGet({
          enrollmentId: enrollmentId,
        })

      return unwrapStandardResponse<EnrollmentResponse>(
        response,
      ) as EnrollmentResponse
    },
    enabled:
      !!enrollmentId &&
      (hasPermission("enrollments:read") ||
        hasPermission("enrollments:read_own")),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new enrollment with validation and error handling
 */
export function useCreateEnrollment() {
  return useApiMutation<EnrollmentResponse, unknown, EnrollmentCreate>({
    mutationFn: async (enrollmentData) => {
      // Let the backend handle permission checks

      // Construct enrollment payload for API
      // Uses local EnrollmentCreate type (optional invoice_data) and
      // passes to generated client which expects its own EnrollmentCreate type
      const enrollmentPayload = {
        student_id: enrollmentData.student_id,
        class_template_id: enrollmentData.class_template_id,
        session_id: enrollmentData.session_id,
        status: enrollmentData.status ?? null,
        notes: enrollmentData.notes ?? null,
        invoice_data: enrollmentData.invoice_data ?? {
          amount_due: 0,
          payment_status: "pending" as const,
        },
      }

      const response =
        await api.enrollments.createEnrollmentApiV1EnrollmentsPost({
          requestBody: enrollmentPayload as any,
        })

      return unwrapStandardResponse<EnrollmentResponse>(
        response,
      ) as EnrollmentResponse
    },
    invalidateQueries: [["enrollments"], ["class-templates"]],
    successToast: (data) => {
      const studentName = data.student_name || "Student"
      const className = data.class_name || "class"

      if (data.status === "confirmed") {
        return {
          title: "Enrollment Confirmed!",
          description: `${studentName} is enrolled in ${className}.`,
        }
      }
      if (data.status === "waitlisted") {
        const position = data.waitlist_position || ""
        return {
          title: "Added to Waitlist",
          description: `Class is full - ${studentName} has been added to the waitlist${position ? ` at position #${position}` : ""}.`,
        }
      }
      return {
        title: "Enrollment Created",
        description: `${studentName} enrollment for ${className} is pending confirmation.`,
      }
    },
    errorToast: true, // Show default error toast on failure
  })
}

/**
 * Update existing enrollment with validation and error handling
 */
export function useUpdateEnrollment() {
  return useApiMutation<
    EnrollmentResponse,
    unknown,
    { enrollmentId: string; enrollmentData: EnrollmentUpdateInput }
  >({
    mutationFn: async ({
      enrollmentId,
      enrollmentData,
    }: {
      enrollmentId: string
      enrollmentData: EnrollmentUpdateInput
    }) => {
      // Let the backend handle permission checks
      // Note: Update does NOT include invoice_data - invoice is updated separately
      const enrollmentUpdate: EnrollmentUpdate = {
        student_id: enrollmentData.student_id,
        class_template_id: enrollmentData.class_template_id,
        session_id: enrollmentData.session_id,
        status: enrollmentData.status,
        notes: enrollmentData.notes,
      }

      const response =
        await api.enrollments.updateEnrollmentApiV1EnrollmentsEnrollmentIdPut({
          enrollmentId: enrollmentId,
          requestBody: enrollmentUpdate,
        })

      return unwrapStandardResponse<EnrollmentResponse>(
        response,
      ) as EnrollmentResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["enrollments", variables.enrollmentId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["enrollments"]],
  })
}

/**
 * Delete enrollment with permission checks and error handling
 */
export function useDeleteEnrollment() {
  return useApiMutation<{ enrollmentId: string }, unknown, string>({
    mutationFn: async (enrollmentId) => {
      // Let the backend handle permission checks
      await api.enrollments.deleteEnrollmentApiV1EnrollmentsEnrollmentIdDelete({
        enrollmentId: enrollmentId,
      })
      return { enrollmentId }
    },
    removeQueries: [(variables) => ["enrollments", variables]],
    invalidateQueries: [["enrollments"]],
  })
}

/**
 * Invalidate enrollments queries to force a refetch
 */
export function useInvalidateEnrollments() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["enrollments"] })
  }
}
